import { yup } from '@framework/validation/initYup.ts';
import { isObjectsArrayField } from '../../datatype/Datatype.ts';
import { type EvaluationScope } from '../../evaluate/EvaluationScope.ts';
import { ExpressionType } from '../../IExpression.ts';
import { createExpressionType } from '../../IExpressionType.ts';
import { type IReferenceObjectValue } from '../../references/IReference.ts';
import {
  expressionLogger,
  expressionLogger as logger,
} from '../../utils/expressionLogger.ts';
import { ConditionExpressionType } from '../condition/ConditionExpression.type.ts';
import { type IConditionExpression } from '../condition/IConditionExpression.ts';
import { type ISubconditionsGroupExpression } from './ISubconditionsGroupExpression.ts';

export const SubconditionGroupExpressionSchema: yup.ObjectSchema<ISubconditionsGroupExpression> =
  yup
    .object({
      type: yup
        .string()
        .exact(ExpressionType.SubconditionsGroup)
        .required()
        .default(ExpressionType.SubconditionsGroup),
      conditions: yup
        .array<any, IConditionExpression>(
          yup.lazy(() => ConditionExpressionType.schema),
        )
        .label('Condizioni elemento')
        .required(),
    })
    .noUnknown();

export const SubconditionGroupExpressionType =
  createExpressionType<ISubconditionsGroupExpression>({
    type: ExpressionType.SubconditionsGroup,
    schema: SubconditionGroupExpressionSchema,
    /**
     * Ritorna il numero di elementi del parent che soddisfano **tutte**
     * le condizioni specificate.
     */
    evaluate(expression, scope) {
      const parent = scope.getParent();

      // biome-ignore format: <tbd>
      expressionLogger.debug(`[SubconditionGroupExpression.evaluate] Valutazione subcondizioni`, { expression, parent });

      if (!parent) {
        return null;
      }

      // Caso normale: array di oggetti
      if (!isObjectsArrayField(parent?.reference.field)) {
        // biome-ignore format: <tbd>
        logger.warn(`Parent is not an object array field`, { parent, expression });
      }

      // Array standard
      if (!(parent && Array.isArray(parent?.value))) {
        return null;
      }

      return (parent.value as IReferenceObjectValue[]).filter(item => {
        return evaluateSubconditionsOnItem(scope, expression, item);
      });
    },
    /**
     * Le sottocondizioni possono avere reference ma non consideriamo quelle
     * nel membro a Sx perché fa riferimento a un campo della lista padre
     */
    getReferences(expression, collect) {
      return expression.conditions.flatMap(cond => {
        return cond.arguments.flatMap(collect);
      });
    },
    // biome-ignore lint/suspicious/noShadowRestrictedNames: vogliamo che rievochi il `toString` passato
    toString(expression, toString) {
      return `( ${expression.conditions.map(toString).join(' && ')} )`;
    },
  });

/**
 * Valuta tutte le condizioni specificate su un elemento.
 * Ritorna `true` se tutte le condizioni sono soddisfatte, `false` altrimenti.
 */
function evaluateSubconditionsOnItem(
  scope: EvaluationScope,
  expression: ISubconditionsGroupExpression,
  item: IReferenceObjectValue,
) {
  // Aggiungiamo le variabili dell'item allo scope corrente, creando uno scope
  // dedicato.
  const itemScope = scope.addChildVariablesFromParent(item);

  for (const condition of expression.conditions) {
    const conditionValue = itemScope.evaluate(condition);
    if (!conditionValue) {
      return false;
    }
  }
  return true;
}
